import { Link, NavLink } from "react-router-dom";
import {
  Bell,
  ChevronLeft,
  CircleUser,
  KeyRound,
  LayoutGrid,
  Plus,
  Smartphone,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { useAddDevice } from "@/hooks/use-add-device";
import { GettingStartedCard } from "@/components/getting-started/getting-started-card";
import type { NavItem, NavQuickAction } from "@/types/navigation";

import { ServiceSelector } from "./service-selector";
import { SubscriptionBanner } from "./subscription-banner";
import { UserMenu } from "./user-menu";

const NAV_ITEMS: NavItem[] = [
  {
    label: "Devices",
    href: "/devices",
    icon: Smartphone,
    create: { label: "Add device", action: "add-device" },
  },
  {
    label: "Notifications",
    href: "/notifications",
    icon: Bell,
    create: { label: "New notification", to: "/notifications/new" },
  },
  {
    label: "Widgets",
    href: "/widgets",
    icon: LayoutGrid,
    create: { label: "New widget", to: "/widgets/new" },
  },
];

// As prefixes, these keep their item active on the pages under them too
// (an API key's detail, create and edit pages).
const SETTINGS_NAV_ITEMS: NavItem[] = [
  { label: "Account", href: "/settings/account", icon: CircleUser },
  {
    label: "API Keys",
    href: "/settings/api-keys",
    icon: KeyRound,
    create: { label: "New API key", to: "/settings/api-keys/new" },
  },
];

const ITEM_CLASS_NAME = cn(
  "flex h-11 items-center gap-2.5 rounded-lg px-2 text-sm font-medium transition-colors md:h-8 md:gap-2 md:text-[13px]",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
);

// The row (group/row) is hovered while the pointer is on its "+" as well, so
// the item stays highlighted behind it.
const INACTIVE_ITEM_CLASS_NAME = cn(
  "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
  "group-hover/row:bg-sidebar-accent group-hover/row:text-sidebar-accent-foreground"
);

const QUICK_ACTION_CLASS_NAME = cn(
  "absolute inset-y-0 right-1.5 my-auto flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-[opacity,background-color,color] md:size-5",
  // Hidden until its row is hovered or holds focus. Touch screens have no
  // hover, so there it always shows, and an invisible overlay grows its
  // touch target without changing the look.
  "pointer-events-none opacity-0 group-hover/row:pointer-events-auto group-hover/row:opacity-100 group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-100",
  "pointer-coarse:pointer-events-auto pointer-coarse:opacity-100 pointer-coarse:after:absolute pointer-coarse:after:-inset-2",
  "hover:bg-black/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring disabled:pointer-events-none"
);

interface QuickAddDeviceButtonProps {
  label: string;
}

function QuickAddDeviceButton({ label }: QuickAddDeviceButtonProps) {
  // The sidebar is always mounted, so appio.js loads on the first click
  // rather than on every dashboard page.
  const { addDevice, isReady } = useAddDevice({ preload: false });

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={addDevice}
      disabled={!isReady}
      data-closes-drawer=""
      className={QUICK_ACTION_CLASS_NAME}
    >
      <Plus aria-hidden="true" className="size-4 md:size-3.5" />
    </button>
  );
}

interface QuickActionProps {
  action: NavQuickAction;
}

function QuickAction({ action }: QuickActionProps) {
  if ("to" in action) {
    return (
      <Link
        to={action.to}
        aria-label={action.label}
        title={action.label}
        className={QUICK_ACTION_CLASS_NAME}
      >
        <Plus aria-hidden="true" className="size-4 md:size-3.5" />
      </Link>
    );
  }
  return <QuickAddDeviceButton label={action.label} />;
}

interface SidebarLinkProps {
  item: NavItem;
}

function SidebarLink({ item }: SidebarLinkProps) {
  const Icon = item.icon;

  return (
    <div className="group/row relative">
      <NavLink
        to={item.href}
        className={({ isActive }) =>
          cn(
            ITEM_CLASS_NAME,
            isActive
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : INACTIVE_ITEM_CLASS_NAME,
            // Room for the "+" so a long label never runs under it.
            item.create && "pr-10 md:pr-8"
          )
        }
      >
        <Icon className="size-4 shrink-0" />
        <span className="truncate">{item.label}</span>
      </NavLink>
      {item.create && <QuickAction action={item.create} />}
    </div>
  );
}

interface AppSidebarProps {
  settings?: boolean;
  backTo?: string;
  showSubscriptionBanner?: boolean;
}

export function AppSidebar({
  settings = false,
  backTo = "/",
  showSubscriptionBanner = true,
}: AppSidebarProps) {
  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="p-3">
        {settings ? (
          <Link to={backTo} className={cn(ITEM_CLASS_NAME, INACTIVE_ITEM_CLASS_NAME)}>
            <ChevronLeft className="size-4 shrink-0" />
            Back to app
          </Link>
        ) : (
          <div className="flex flex-col gap-3">
            <ServiceSelector />
            <GettingStartedCard />
          </div>
        )}
      </div>

      <nav
        aria-label={settings ? "Settings" : "Main"}
        className="flex-1 overflow-y-auto px-3 py-1"
      >
        {settings && (
          <p className="flex h-8 items-center px-2 text-xs text-muted-foreground">
            Settings
          </p>
        )}
        <ul className="flex flex-col gap-0.5">
          {(settings ? SETTINGS_NAV_ITEMS : NAV_ITEMS).map((item) => (
            <li key={item.href}>
              <SidebarLink item={item} />
            </li>
          ))}
        </ul>
      </nav>

      <div className="flex flex-col gap-0.5 p-3">
        {showSubscriptionBanner && <SubscriptionBanner />}
        <UserMenu />
      </div>
    </div>
  );
}
